import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Customer, CustomerListScope, SortDirection } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { MultiSelectSearch } from '../../components/ui/MultiSelectSearch.js';
import { formatDate } from '../../utils/date.js';
import { useRowLink } from '../../utils/useRowLink.js';
import { CustomerFormModal } from './CustomerFormModal.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import { useCustomer, useCustomers } from './api.js';
import { useCustomerTypeOptions } from '../customer-types/api.js';
import { FilterDefaultsButtons } from '../filters/FilterDefaultsButtons.js';
import { savedStringArray, useFilterDefaults } from '../filters/useFilterDefaults.js';

/** Column keys the customers list can be sorted by (mirrors the backend allow-list). */
type CustomerSortKey = 'name' | 'openOpps' | 'activeProjects' | 'openTickets' | 'lpt' | 'lst';

type CustomerSort = { key: CustomerSortKey; dir: SortDirection };

/** A clickable table header that toggles server-side sorting for its column. */
function SortableHeader({
  label,
  sortKey,
  sort,
  onToggle,
  align = 'left',
  title,
}: {
  label: string;
  sortKey: CustomerSortKey;
  sort: CustomerSort;
  onToggle: (key: CustomerSortKey) => void;
  align?: 'left' | 'right';
  title?: string;
}): JSX.Element {
  const active = sort.key === sortKey;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`} title={title}>
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={`inline-flex items-center gap-1 uppercase tracking-wide ${
          active ? 'text-slate-700' : 'hover:text-slate-700'
        }`}
        aria-label={`Sort by ${title ?? label}`}
      >
        {label}
        <span className={`text-[10px] ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
}

export interface CustomerListViewProps {
  /** Heading shown at the top of the page. */
  title: string;
  /** Subheading describing the list. */
  description: string;
  /** Server-side scope controlling which customer types are hidden. */
  scope: CustomerListScope;
  /** Customer type names hidden from this view (used to trim the filter dropdown). */
  hiddenTypeNames: readonly string[];
  /** Label for the create button / empty-state action. */
  createLabel: string;
  /** Route base for deep links to a single record (e.g. `/customers`, `/sales/prospects`). */
  basePath: string;
}

export function CustomerListView({
  title,
  description,
  scope,
  hiddenTypeNames,
  createLabel,
  basePath,
}: CustomerListViewProps): JSX.Element {
  const { can } = useAuth();
  const canEdit = can('customers', 'edit');
  const navigate = useNavigate();
  const rowLink = useRowLink();
  const { id: routeCustomerId } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [sort, setSort] = useState<CustomerSort>({ key: 'name', dir: 'asc' });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [returnTab, setReturnTab] = useState<string>('details');
  const [backOnboardingId, setBackOnboardingId] = useState<string | null>(null);
  const [pendingCustomerId, setPendingCustomerId] = useState<string | null>(null);

  // Per-scope key so the Customers and Prospects views keep separate defaults.
  const filterDefaults = useFilterDefaults(`customers:${scope}`, (saved) => {
    const savedTypes = savedStringArray(saved, 'typeIds');
    if (savedTypes !== null) setTypeIds(savedTypes);
  });

  useTrackRecent(
    formOpen && editing
      ? {
          entity: 'customer',
          id: editing.id,
          label: editing.name,
          path: `/customers?returnCustomerId=${editing.id}&returnTab=details`,
        }
      : null,
  );

  const query = useCustomers({
    page,
    pageSize: DEFAULT_PAGE_SIZE,
    search: search || undefined,
    typeIds: typeIds.length > 0 ? typeIds : undefined,
    scope,
    sortBy: sort.key,
    sortDir: sort.dir,
  });
  const typeOptions = useCustomerTypeOptions();
  const typeNameById = new Map((typeOptions.data ?? []).map((type) => [type.id, type.name]));

  const hiddenSet = useMemo(() => new Set(hiddenTypeNames), [hiddenTypeNames]);
  const filterTypes = (typeOptions.data ?? []).filter((type) => !hiddenSet.has(type.name));

  const customers = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  // Capture a return target from query params (e.g. “Go to customer” / “Back to customer”),
  // then fetch that customer directly by id so the modal opens even when the customer is
  // not on the current list page (or is a hidden type excluded from this scope).
  useEffect(() => {
    const returnCustomerId = searchParams.get('returnCustomerId');
    if (returnCustomerId) {
      setReturnTab(searchParams.get('returnTab') ?? 'details');
      setBackOnboardingId(searchParams.get('returnOnboardingId'));
      setPendingCustomerId(returnCustomerId);
      // Clean up query params now that we've captured everything we need.
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const pendingCustomerQuery = useCustomer(pendingCustomerId ?? undefined);
  useEffect(() => {
    if (pendingCustomerId && pendingCustomerQuery.data) {
      setEditing(pendingCustomerQuery.data);
      setFormOpen(true);
      setPendingCustomerId(null);
    }
  }, [pendingCustomerId, pendingCustomerQuery.data]);

  // Route-based deep-link: /customers/:id (or /sales/prospects/:id) opens that record.
  useEffect(() => {
    if (routeCustomerId) setPendingCustomerId(routeCustomerId);
  }, [routeCustomerId]);

  const handleSearchSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const toggleType = (id: string, checked: boolean): void => {
    setPage(1);
    setTypeIds((prev) => (checked ? [...prev, id] : prev.filter((t) => t !== id)));
  };

  const toggleSort = (key: CustomerSortKey): void => {
    setPage(1);
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  };

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  // Closing returns the address bar to the list when opened via a deep link.
  const closeForm = (): void => {
    setFormOpen(false);
    setBackOnboardingId(null);
    setEditing(null);
    if (routeCustomerId) navigate(basePath);
  };

  return (
    <div>
      <PageHeader
        title={title}
        description={description}
        actions={
          canEdit ? (
            <Button onClick={openCreate}>
              <Icon name="customers" className="h-4 w-4" />
              {createLabel}
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 p-4">
          <div className="flex flex-wrap items-start gap-2">
            <form
              onSubmit={handleSearchSubmit}
              className="flex max-w-md flex-1 items-center gap-2 pt-6"
            >
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name, AKA, or NetSuite account name…"
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>
            <div className="w-72">
              <MultiSelectSearch
                label="Customer types"
                options={filterTypes}
                selected={typeIds}
                onToggle={toggleType}
                emptyText="No customer types."
                placeholder="Filter by type…"
              />
            </div>
            <div className="pt-6">
              <FilterDefaultsButtons
                handle={filterDefaults}
                onSave={() => filterDefaults.save({ typeIds })}
              />
            </div>
          </div>
        </div>

        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <SortableHeader label="Name" sortKey="name" sort={sort} onToggle={toggleSort} />
              <th className="px-4 py-3 font-medium">Type</th>
              <SortableHeader
                label="Open Opps"
                sortKey="openOpps"
                sort={sort}
                onToggle={toggleSort}
                align="right"
                title="Open opportunities"
              />
              <SortableHeader
                label="Active Projects"
                sortKey="activeProjects"
                sort={sort}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="LPT"
                sortKey="lpt"
                sort={sort}
                onToggle={toggleSort}
                title="Latest project time entry"
              />
              <SortableHeader
                label="Open Tickets"
                sortKey="openTickets"
                sort={sort}
                onToggle={toggleSort}
                align="right"
              />
              <SortableHeader
                label="LST"
                sortKey="lst"
                sort={sort}
                onToggle={toggleSort}
                title="Latest support time entry"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={7}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={customers.length === 0}
              emptyTitle="No customers found"
              emptyDescription={
                search || typeIds.length > 0
                  ? 'Try a different search term or filter.'
                  : 'Create your first record to get started.'
              }
              emptyAction={
                canEdit && !search && typeIds.length === 0 ? (
                  <Button onClick={openCreate}>{createLabel}</Button>
                ) : undefined
              }
            >
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="cursor-pointer hover:bg-slate-50"
                  {...rowLink(`${basePath}/${customer.id}`)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{customer.name}</td>
                  <td className="px-4 py-3">
                    {customer.typeIds.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {customer.typeIds.map((id) => (
                          <Badge key={id} tone="blue">
                            {typeNameById.get(id) ?? 'Unknown'}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {customer.openOpportunitiesCount ?? 0}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {customer.activeProjectsCount ?? 0}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDate(customer.latestProjectTimeDate)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                    {customer.openTicketsCount ?? 0}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDate(customer.latestSupportTimeDate)}
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && customers.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <CustomerFormModal
        isOpen={formOpen}
        customer={editing}
        onClose={closeForm}
        initialTab={returnTab}
        headerActions={
          backOnboardingId ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/sales/onboarding/${backOnboardingId}`)}
            >
              ← Back to onboarding
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}
